import type { ProjectOnTimeTasks } from '@interfaces/IProject';
import type { ProductivityMetricTone } from '@interfaces/IProductivity';
import type { MetricTone } from '../MetricBar/MetricBar';

/** Actual against plan: 6+ points behind is late, 2+ is a watch — the same
 *  bands the schedule status chip uses. */
export function progressTone(actual: number, plan: number): MetricTone {
  const gap = plan - actual;
  if (gap >= 6) return 'bad';
  if (gap >= 2) return 'warn';
  return 'good';
}

/** Points ahead (+) or behind (−) plan, rounded. */
export function progressGap(actual: number, plan: number): number {
  return Math.round(actual - plan);
}

/** The Productivity page's health tone, on the card's scale. */
export function productivityTone(tone: ProductivityMetricTone): MetricTone {
  if (tone === 'red') return 'bad';
  if (tone === 'amber') return 'warn';
  if (tone === 'green') return 'good';
  return 'neutral';
}

/** The on-time share, or `null` when nothing was due (no share to show). */
export function onTimePct(tasks: ProjectOnTimeTasks): number | null {
  return tasks.due === 0 ? null : Math.round((tasks.onTime / tasks.due) * 100);
}

/** Under half on time is bad, under three quarters a watch. */
export function onTimeTone(pct: number): MetricTone {
  if (pct < 50) return 'bad';
  if (pct < 75) return 'warn';
  return 'good';
}

/** How far either side of the expected workforce still counts as right. */
const WORKFORCE_GAP = 0.15;

/**
 * The workforce bar against its plan: the track spans 0 → 1.5× expected (or
 * the headcount, if more), the tick marks expected and the band its ±15%.
 * Outside the band is a watch either way — under-staffed slips, over-staffed
 * costs.
 */
export function workforceBar(workers: number, expected: number) {
  const scale = Math.max(expected * 1.5, workers);
  const pct = (n: number) => (n / scale) * 100;
  const inBand = Math.abs(workers - expected) <= expected * WORKFORCE_GAP;
  return {
    fillPct: pct(workers),
    tickPct: pct(expected),
    band: { fromPct: pct(expected * (1 - WORKFORCE_GAP)), toPct: pct(expected * (1 + WORKFORCE_GAP)) },
    tone: (inBand ? 'neutral' : 'warn') as MetricTone,
  };
}
